import { KitType } from '@repo/types';
import { Banner } from '@repo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import c from 'classnames';
import {
  ButtonColors,
  ButtonVariants,
  Card,
  ContainerSizes,
  DesignButton,
  FAQ,
  FeedbackWidget,
  Icon,
  Markdown,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { Suspense, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import { getConditionActionPlanQuery } from 'services/conditions/conditionsActionPlan';
import {
  getSingleConditionMetricsQuery,
  type SingleConditionData,
} from 'services/conditions/useConditionMetrics';
import useFeature from 'services/features/useFeature';
import { ActionPlanItem } from 'types/ActionPlan';
import { NewCondition } from 'types/Conditions';
import { FeedbackPageType } from 'types/Feedback';
import conditionSubject from 'utils/conditions/conditionSubject';
import formatConditionName from 'utils/conditions/formatConditionName';
import { RelatedActions } from '../../Insights/SubMetricDetail/RelatedActions';
import SensitiveConditionsReminderModal from '../SensitiveConditionsReminderModal';
import { LegacyConditionMetricsAssociated } from './ConditionMetricsAssociated.legacy';
import styles from './ConditionMetricsDetailView.legacy.module.css';

export const LegacyConditionMetricsDetailView = () => {
  const { currentKitId, currentKitType, currentKit, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const { condition } = useParams<{ condition: string }>();
  if (!condition) throw new Error('No condition provided');

  const { isFeatureEnabled: isSensitiveModalEnabled } = useFeature(
    'sensitive_conditions_reminder_modal',
  );

  const { data: conditionData } = useSuspenseQuery<SingleConditionData | null>(
    getSingleConditionMetricsQuery(currentKitId, condition),
  );

  const { data: actionPlanData } = useActionPlanData({ kitId: currentKitId });
  const { data: conditionActionPlanData } = useSuspenseQuery(
    getConditionActionPlanQuery(currentKitId),
  );

  // Action items to show in the second column
  const actionItems = useMemo(() => {
    const seenIds = new Set<number | string>();
    const uniqueActions: ActionPlanItem[] = [];

    (conditionData?.metrics ?? []).forEach(m => {
      const actionsForMetric = getActionsForSubMetric(
        actionPlanData ?? [],
        m.metric_id,
      );

      actionsForMetric.forEach(action => {
        if (!seenIds.has(action.id)) {
          seenIds.add(action.id);
          uniqueActions.push(action);
        }
      });
    });

    return uniqueActions;
  }, [actionPlanData, conditionData]);

  const currentCondition: NewCondition | null = conditionData
    ? ({
        condition_id: conditionData.condition_id,
        condition: conditionData.condition,
        condition_display_name: conditionData.condition_display_name,
        about: conditionData.about,
        faq: conditionData.faq,
        references: conditionData.references,
        id: conditionData.condition_id,
      } as any)
    : null;

  // Map action plan items to the format expected by ConditionMetricActions
  // Uses conditionActionPlanData for ordering, then enriches with actionPlanData details
  // Match logic: check if condition name contains the action's tags (backend logic in condition.ts line 473)
  const matchedActions = useMemo(() => {
    if (!actionPlanData || !conditionActionPlanData) return [];
    return conditionActionPlanData
      .map((action: any) => {
        const matchedAction = actionPlanData.find(
          (actionPlanItem: any) =>
            actionPlanItem.id === +action.action_plan_recommendations_id,
        );
        return matchedAction
          ? {
              ...action,
              display_title: matchedAction.display_title,
              category: matchedAction.category,
            }
          : null;
      })
      .filter((action: any) => {
        // Check if the condition name contains the action's tags
        return condition.includes(action?.tags || '');
      })
      .filter((action: any) => action !== null);
  }, [actionPlanData, conditionActionPlanData, condition]);

  const actionsRef = useRef<HTMLDivElement>(null);

  if (!currentKitId || !currentCondition) return <Spinfinity />;

  const isGut = currentKitType === KitType.STOOL;

  const formattedConditionName = formatConditionName(
    conditionData?.condition_display_name,
  );

  return (
    <PageContainer size={ContainerSizes.LG}>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{
          [conditionData?.condition ?? '']:
            conditionData?.condition_display_name,
        }}
      />
      <Banner
        variant='full'
        title='This is not a diagnosis.'
        color='purple'
        className={styles.banner}
        content={`Research shows that ${formattedConditionName} is linked to ${isGut ? 'gut' : 'vaginal'} microbiome differences, but we don't know if one causes the other. Having microbiome associations do not mean ${conditionSubject(currentKit?.age_at_sampling, currentTinyAccount?.first_name)} or will develop ${formattedConditionName}.`}
      />
      {conditionData?.sensitive && isSensitiveModalEnabled && (
        <SensitiveConditionsReminderModal
          conditionName={conditionData.condition_display_name}
          conditionId={`${conditionData.condition_id}`}
        />
      )}
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <div className={styles.contentContainer}>
            <Suspense fallback={<Spinfinity />}>
              {matchedActions.length > 0 && (
                <Card className={c(styles.actionsReminder, 'hide-on-desktop')}>
                  <div
                    className='flex gap-2'
                    onClick={() => {
                      if (actionsRef.current) {
                        actionsRef.current.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }
                    }}
                  >
                    <div className={styles.iconWrapper}>
                      <Icon name='flashFilled' size='m' />
                    </div>
                    <div className='flex flex-column gap-1'>
                      <Typography variant='heading-s'>{`${matchedActions.length} actions related to this metric`}</Typography>
                      <DesignButton
                        label='Jump to actions'
                        iconNameRight='chevronForward'
                        color={ButtonColors.WHITE}
                        variant={ButtonVariants.TEXT}
                      />
                    </div>
                  </div>
                </Card>
              )}
              <LegacyConditionMetricsAssociated
                condition={currentCondition}
                conditionData={conditionData}
              />
            </Suspense>

            {!!currentCondition?.about && (
              <Card variant='wrapper' className={styles.aboutCard}>
                <div className={styles.secondaryText}>
                  <Markdown
                    customOverride={{
                      h3: ({ children }: { children: string }) => (
                        <Typography variant='heading-l'>{children}</Typography>
                      ),
                    }}
                  >
                    {currentCondition.about}
                  </Markdown>
                </div>
              </Card>
            )}
            <div ref={actionsRef} className='hide-on-desktop'>
              <RelatedActions actions={actionItems} />
            </div>
            {currentCondition?.faq && (
              <Card className={styles.faqCard} variant='wrapper'>
                <Typography variant='heading-l'>
                  Frequently Asked Questions
                </Typography>
                <FAQ faq={currentCondition.faq} hideLastDivider />
              </Card>
            )}
            {currentCondition?.references && (
              <Card className={styles.referenceCard} variant='wrapper'>
                <Typography
                  variant='heading-l'
                  className={styles.referenceTitle}
                >
                  References
                </Typography>
                <Markdown>{currentCondition.references}</Markdown>
              </Card>
            )}
            <FeedbackWidget
              kitId={currentKitId}
              pageType={FeedbackPageType.CONDITION}
              pageId={currentCondition?.id}
            />
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <div className='hide-on-mobile'>
            <RelatedActions actions={actionItems} />
          </div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
